import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import UserFormModal from '../components/UserFormModal';
import UserDeleteModal from '../components/UserDeleteModal';
import Pagination from '../components/Pagination';
import './Users.css';

const Users = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searching, setSearching] = useState('');
  const [filterRole, setFilterRole] = useState('todos');

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Verificar permisos
  useEffect(() => {
    if (user?.role !== 'superadmin' && user?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/auth/users', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError('Error al cargar usuarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (userToEdit = null) => {
    setEditingUser(userToEdit);
    setShowFormModal(true);
  };

  const handleCloseForm = () => {
    setEditingUser(null);
    setShowFormModal(false);
  };

  const handleSaveUser = async (data) => {
    try {
      if (editingUser) {
        await axios.put(`/api/auth/users/${editingUser.id}`, data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
      } else {
        await axios.post('/api/auth/create-user', data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
      }
      fetchUsers();
      handleCloseForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar usuario');
    }
  };

  const handleOpenDelete = (userToDelete) => {
    setUserToDelete(userToDelete);
    setShowDeleteModal(true);
  };

  const handleCloseDelete = () => {
    setUserToDelete(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/api/auth/users/${userToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      fetchUsers();
      handleCloseDelete();
    } catch (err) {
      setError('Error al eliminar usuario');
    }
  };

  const getFilteredUsers = () => {
    let filtered = users;

    if (searching.trim()) {
      const term = searching.toLowerCase();
      filtered = filtered.filter(u =>
        u.username.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term)
      );
    }

    if (filterRole !== 'todos') {
      filtered = filtered.filter(u => u.role === filterRole);
    }

    return filtered;
  };

  const filteredUsers = getFilteredUsers();

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searching, filterRole]);

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />

      <div className="main-content">
        <Navbar title="👥 Gestión de Usuarios" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          <div className="page-header">
            <div className="header-title">
              <h2>Usuarios del Sistema</h2>
              <p className="subtitle">Total: {users.length} usuarios registrados</p>
            </div>
            <button onClick={() => handleOpenForm()} className="btn-primary">
              ➕ Nuevo Usuario
            </button>
          </div>

          <div className="filters-bar">
            <div className="filter-group">
              <label>🔍 Buscar:</label>
              <input
                type="text"
                placeholder="Usuario o correo..."
                value={searching}
                onChange={(e) => setSearching(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>🔓 Rol:</label>
              <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="superadmin">Superadmin</option>
                <option value="admin">Admin</option>
                <option value="user">Usuario</option>
              </select>
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando usuarios...</p>
          ) : filteredUsers.length === 0 ? (
            <p className="no-data">No hay usuarios que coincidan con los filtros</p>
          ) : (
            <div className="table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Perfil</th>
                    <th>Asociación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(u => (
                    <tr key={u.id}>
                      <td className="user-name">{u.username}</td>
                      <td className="user-email">{u.email}</td>
                      <td>
                        <span className={`role-badge role-${u.role}`}>
                          {u.role === 'superadmin' ? '🔐 Superadmin' : u.role === 'admin' ? '👨‍💼 Admin' : '👤 Usuario'}
                        </span>
                      </td>
                      <td>
                        {u.profile_name ? (
                          <span className="profile-badge">🛡️ {u.profile_name}</span>
                        ) : (
                          <span className="no-profile">Ninguno</span>
                        )}
                      </td>
                      <td>
                        {u.transito_name ? (
                          <span className="transito-badge">🏢 {u.transito_name}</span>
                        ) : u.concesionario_name ? (
                          <span className="concesionario-badge">🏪 {u.concesionario_name}</span>
                        ) : (
                          <span className="no-transito">Global</span>
                        )}
                      </td>
                      <td className="actions">
                        <button
                          onClick={() => handleOpenForm(u)}
                          className="btn-action btn-edit"
                          title="Editar usuario"
                        >
                          ✏️ Editar
                        </button>
                        {u.id !== user?.id && (
                          <button
                            onClick={() => handleOpenDelete(u)}
                            className="btn-action btn-delete"
                            title="Eliminar usuario"
                          >
                            🗑️ Eliminar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <Pagination 
                currentPage={currentPage}
                totalItems={filteredUsers.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>

        <UserFormModal
          isOpen={showFormModal}
          user={editingUser}
          onClose={handleCloseForm}
          onSubmit={handleSaveUser}
        />

        <UserDeleteModal
          isOpen={showDeleteModal}
          user={userToDelete}
          onClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
};

export default Users;
